import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { publicApiClient } from '@/lib/api/client';
import { tableBookingTicketService } from '@/features/booking/services/tableBookingTicketService';
import { cartService } from '@/features/cart/services/cartService';
import { authService } from '@/features/user/services/authService';
import { useAuth } from '@/hooks/useAuth';
import { CartStatus } from '@/features/cart/types';
import type {
  TableBookingTicketState,
  TableTimeSlotSelection,
  TableBookingPriceBreakdown,
  EventScheduleData,
  EventBookingCategoryData,
} from '@/features/booking/types/tableBookingTicketTypes';
import type { EventBookingCategoryWithAddOnSchedulesResponse } from '@/features/booking/types/eventBookingCategoryTypes';
import type { Business } from '@/features/booking/types/tableBookingTypes';
import type { CartResponse, CartItemResponse } from '@/features/cart/types';
import type { AddOnScheduleData, AddOnSelection } from '@/features/booking/types/ticketBookingTypes';
import { formatISOWithTimezone } from '@/lib/utils/timeZone';


// Helper function to create a time slot key from start time
const getTimeSlotKey = (startTime: string): string => {
  return new Date(startTime).toISOString();
};

// Helper function to check if two time slots overlap
// Duration is in nanoseconds (Go's time.Duration JSON format)
const doTimeSlotsOverlap = (
  start1: string,
  duration1Nanoseconds: number,
  start2: string,
  duration2Nanoseconds: number
): boolean => {
  const start1Time = new Date(start1).getTime();
  // Convert nanoseconds to milliseconds: nanoseconds / 1e6
  const end1Time = start1Time + duration1Nanoseconds / 1e6;
  const start2Time = new Date(start2).getTime();
  const end2Time = start2Time + duration2Nanoseconds / 1e6;

  // Overlap if: start1 < end2 && start2 < end1
  return start1Time < end2Time && start2Time < end1Time;
};

// Helper function to calculate end time (duration is in nanoseconds)
const calculateEndTime = (startTime: string, durationNanoseconds: number): string => {
  const start = new Date(startTime);
  // Convert nanoseconds to milliseconds: nanoseconds / 1e6
  const end = new Date(start.getTime() + durationNanoseconds / 1e6);
  return end.toISOString();
};

export const useTableBookingTicket = (
  eventBookingCategoryId: string,
  initialCartTab?: 'main' | 'guest' | null,
  cartItemId?: string | null
) => {
  const { isAuthenticated, isGuest, refetch: refetchAuth } = useAuth();
  const [state, setState] = useState<TableBookingTicketState>({
    eventSchedule: null,
    eventBookingCategory: null,
    tableAvailability: {},
    selections: new Map(),
    selectedTable: null,
    selectedTimeSlot: null,
    loading: true,
    error: null,
    syncingCart: false,
  });

  // Cart-related state
  const [incompleteCarts, setIncompleteCarts] = useState<CartResponse[] | null>(null);
  const [selectedCartTab, setSelectedCartTab] = useState<'main' | 'guest' | null>(null);
  const [isLoadingCarts, setIsLoadingCarts] = useState(false);
  const isLoadingCartsRef = useRef(false);
  const isSyncingCartRef = useRef(false);
  const isTabSwitchingRef = useRef(false);

  // Per-tab saved local state — preserves local edits when switching tabs
  const savedSelectionsPerTab = useRef<{
    main: {
      selections: Map<string, Map<string, TableTimeSlotSelection>>;
      selectedTable: string | null;
      selectedTimeSlot: string | null;
    } | null;
    guest: {
      selections: Map<string, Map<string, TableTimeSlotSelection>>;
      selectedTable: string | null;
      selectedTimeSlot: string | null;
    } | null;
  }>({ main: null, guest: null });

  // Add-on selection error display
  const [addOnErrorMessage, setAddOnErrorMessage] = useState<string | null>(null);
  const errorMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track if user has manually clicked on a table (distinguishes auto-selected from user-clicked)
  const [tableClicked, setTableClicked] = useState(false);

  // Track if we've already attempted auto-selection (to prevent re-selection after cart items are removed)
  const hasAttemptedAutoSelectionRef = useRef(false);

  // Helper function to get current cart based on selected tab
  const getCurrentCart = useCallback((): CartResponse | null => {
    if (!incompleteCarts || incompleteCarts.length === 0 || !selectedCartTab) {
      return null;
    }

    if (selectedCartTab === 'main') {
      return incompleteCarts.find(c => !c.is_guest_cart) || null;
    } else {
      return incompleteCarts.find(c => c.is_guest_cart) || null;
    }
  }, [incompleteCarts, selectedCartTab]);

  // Helper function to determine default tab
  const determineDefaultTab = useCallback((carts: CartResponse[]): 'main' | 'guest' | null => {
    if (carts.length === 0) {
      return null;
    }

    const mainCart = carts.find(c => !c.is_guest_cart);
    const guestCart = carts.find(c => c.is_guest_cart);

    if (initialCartTab) {
      if (initialCartTab === 'main' && mainCart) {
        return 'main';
      }
      if (initialCartTab === 'guest' && guestCart) {
        return 'guest';
      }
    }

    if (mainCart && guestCart) {
      return 'main';
    }
    if (guestCart) {
      return 'guest';
    }
    if (mainCart) {
      return 'main';
    }

    return null;
  }, [initialCartTab]);

  // Fetch data on mount and when dependencies change
  const fetchData = useCallback(async () => {
    if (!eventBookingCategoryId) {
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    type EventBookingCategoryResult = {
      eventScheduleData: EventScheduleData;
      eventBookingCategoryData: EventBookingCategoryData;
    };
    const promises: Array<Promise<EventBookingCategoryResult | CartResponse[] | Record<string, number[]>>> = [];

    // Promise 1: Fetch event booking category
    const eventBookingCategoryPromise = (async (): Promise<EventBookingCategoryResult> => {
      try {
        const response = await publicApiClient.get<EventBookingCategoryWithAddOnSchedulesResponse>(
          `/api/public/v1/event-booking-category/${eventBookingCategoryId}`
        );

        if (!response || !response.data || !response.data.event_booking_category) {
          throw new Error('Invalid response structure from API');
        }

        // Check if it's a table booking
        if (!response.data.event_booking_category.booking_category.is_table_booking) {
          throw new Error('This is not a table booking category');
        }

        const eventScheduleFromResponse = response.data.event_booking_category.event_schedule;
        const eventScheduleData: EventScheduleData = {
          id: eventScheduleFromResponse.id,
          start_time: eventScheduleFromResponse.start_time,
          duration: eventScheduleFromResponse.duration,
          early_entry_duration: eventScheduleFromResponse.early_entry_duration,
          cancelled: eventScheduleFromResponse.cancelled,
          event: {
            id: eventScheduleFromResponse.event.id,
            name: eventScheduleFromResponse.event.name,
            description: eventScheduleFromResponse.event.description,
            card_image_id: eventScheduleFromResponse.event.card_image_id,
            base_image_id: eventScheduleFromResponse.event.base_image_id,
            base_video_id: eventScheduleFromResponse.event.base_video_id,
            business: eventScheduleFromResponse.event.business ? {
              id: eventScheduleFromResponse.event.business.id,
              name: eventScheduleFromResponse.event.business.name,
              handle: eventScheduleFromResponse.event.business.handle,
              card_image_id: eventScheduleFromResponse.event.business.card_image_id,
              base_image_id: eventScheduleFromResponse.event.business.base_image_id,
              base_video_id: eventScheduleFromResponse.event.business.base_video_id,
              location: {
                city: eventScheduleFromResponse.event.business.location.city,
                timezone_id: eventScheduleFromResponse.event.business.location.timezone_id
              }
            } : null
          },
          timezone_id: eventScheduleFromResponse.timezone_id,
          add_on_schedules: []
        };

        const addOnSchedules: AddOnScheduleData[] = (response.data.add_on_schedules || []).map(aos => ({
          price: aos.price,
          inventory_count: aos.inventory_count,
          remaining_inventory: aos.remaining_inventory,
          add_on: {
            id: aos.add_on.id,
            name: aos.add_on.name,
            description: aos.add_on.description,
            published: aos.add_on.published,
            default_price: aos.add_on.default_price,
            default_inventory_count: aos.add_on.default_inventory_count,
            image_id: aos.add_on.image_id,
            business: aos.add_on.business as Business | null,
            allow_multiple_per_guest: (aos.add_on as { allow_multiple_per_guest?: boolean }).allow_multiple_per_guest ?? false,
            is_platform_add_on: (aos.add_on as { is_platform_add_on?: boolean }).is_platform_add_on ?? false
          },
          event_schedule: null
        }));

        const ebc = response.data.event_booking_category;
        const eventBookingCategoryData: EventBookingCategoryData = {
          id: ebc.id,
          cancelled: ebc.cancelled,
          conflict_detected: ebc.conflict_detected,
          capacity: ebc.capacity,
          remaining_capacity: ebc.remaining_capacity,
          price: ebc.price,
          cancellation_fees: ebc.cancellation_fees,
          custom_slot_duration: ebc.custom_slot_duration || null,
          booking_category: {
            id: ebc.booking_category.id,
            name: ebc.booking_category.name,
            description: ebc.booking_category.description,
            is_table_booking: ebc.booking_category.is_table_booking,
            card_image_id: ebc.booking_category.card_image_id,
            card_video_id: ebc.booking_category.card_video_id,
            base_image_id: ebc.booking_category.base_image_id ?? null,
            base_video_id: ebc.booking_category.base_video_id ?? null,
            item_type: ebc.booking_category.item_type || 'TABLE',
            perks: ebc.booking_category.perks || [],
            allowed_areas: ebc.booking_category.allowed_areas || null,
            class: ebc.booking_category.class,
            table_categories: ebc.booking_category.table_categories,
            business: ebc.booking_category.business ? {
              id: ebc.booking_category.business.id,
              name: ebc.booking_category.business.name,
              handle: ebc.booking_category.business.handle,
              location: {
                city: ebc.booking_category.business.location.city,
                timezone_id: ebc.booking_category.business.location.timezone_id
              }
            } : null,
            media: (ebc.booking_category.media || []).map(m => ({
              media_id: m.media_id,
              media_type: m.media_type,
              rank: m.rank ?? 0
            }))
          },
          currency: ebc.currency || 'USD',
          add_on_schedules: addOnSchedules
        };

        return { eventScheduleData, eventBookingCategoryData };
      } catch (error) {
        console.error('Error fetching event booking category:', error);
        throw error;
      }
    })();
    promises.push(eventBookingCategoryPromise);

    // Promise 2: Fetch table availability as slot indices
    const tableAvailabilityPromise = (async (): Promise<Record<string, number[]>> => {
      try {
        return await tableBookingTicketService.getTableAvailability(eventBookingCategoryId);
      } catch (error) {
        console.error('Error fetching table availability:', error);
        return {};
      }
    })();
    promises.push(tableAvailabilityPromise);

    // Promise 3: Fetch incomplete carts (only if authenticated)
    let incompleteCartsPromise: Promise<CartResponse[]> | null = null;
    if (isAuthenticated && !isLoadingCartsRef.current) {
      isLoadingCartsRef.current = true;
      setIsLoadingCarts(true);
      incompleteCartsPromise = (async (): Promise<CartResponse[]> => {
        try {
          const carts = await cartService.getIncompleteCartsForEBC(eventBookingCategoryId);

          if (carts.length > 2) {
            console.error(`Unexpected: Found ${carts.length} incomplete carts for EBC. Taking most recent 2.`);
            const sortedCarts = [...carts].sort((a, b) => {
              return new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime();
            });
            carts.splice(0, carts.length, ...sortedCarts.slice(0, 2));
          }

          return carts;
        } catch (error) {
          console.error('Error fetching incomplete carts:', error);
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            return [];
          }
          throw error;
        }
      })();
      promises.push(incompleteCartsPromise);
    }

    // Execute all promises in parallel
    try {
      const results = await Promise.allSettled(promises);

      // Process event booking category result
      const ebcResult = results[0];
      if (ebcResult.status === 'fulfilled') {
        const result = ebcResult.value as EventBookingCategoryResult;
        const { eventScheduleData, eventBookingCategoryData } = result;
        setState(prev => ({
          ...prev,
          eventSchedule: eventScheduleData,
          eventBookingCategory: eventBookingCategoryData,
        }));
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
          error: ebcResult.reason instanceof Error ? ebcResult.reason.message : 'Failed to fetch booking category'
        }));
        return;
      }

      // Process table availability result (slot indices)
      const availabilityResult = results[1];
      if (availabilityResult.status === 'fulfilled') {
        const availability = availabilityResult.value as Record<string, number[]>;
        setState(prev => ({ ...prev, tableAvailability: availability }));
      }

      // Process incomplete carts result (if it was executed)
      if (incompleteCartsPromise && results.length > 2) {
        const cartsResult = results[2];
        if (cartsResult.status === 'fulfilled') {
          const carts = cartsResult.value as CartResponse[];
          setIncompleteCarts(carts);
          const defaultTab = determineDefaultTab(carts);
          setSelectedCartTab(defaultTab);
        } else {
          const errorMessage = cartsResult.reason instanceof Error ? cartsResult.reason.message : 'Unknown error';
          if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            setTimeout(async () => {
              try {
                if (eventBookingCategoryId && !isLoadingCartsRef.current) {
                  isLoadingCartsRef.current = true;
                  const retryCarts = await cartService.getIncompleteCartsForEBC(eventBookingCategoryId);
                  setIncompleteCarts(retryCarts);
                  const defaultTab = determineDefaultTab(retryCarts);
                  setSelectedCartTab(defaultTab);
                  isLoadingCartsRef.current = false;
                }
              } catch (retryError) {
                console.error('Retry failed for incomplete carts:', retryError);
                setIncompleteCarts([]);
                isLoadingCartsRef.current = false;
              }
            }, 1000);
          } else {
            setIncompleteCarts([]);
          }
        }
        setIsLoadingCarts(false);
        isLoadingCartsRef.current = false;
      }

      setState(prev => ({ ...prev, loading: false }));
    } catch (error) {
      console.error('Unexpected error in fetchData:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch data'
      }));
      setIsLoadingCarts(false);
      isLoadingCartsRef.current = false;
    }
  }, [eventBookingCategoryId, isAuthenticated, determineDefaultTab]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBookingCategoryId, isAuthenticated]);

  // Preselect cart items when cart exists
  useEffect(() => {
    // Skip reloading if we're currently syncing to prevent overwriting local changes
    // Use state.syncingCart instead of ref to ensure we can properly track sync state
    if (state.syncingCart || isSyncingCartRef.current) {
      isTabSwitchingRef.current = false;
      return;
    }

    if (!incompleteCarts || incompleteCarts.length === 0 || !state.eventBookingCategory || !selectedCartTab) {
      isTabSwitchingRef.current = false;
      return;
    }

    // Check if there's saved local state for this tab (user had local edits before switching away)
    const savedState = savedSelectionsPerTab.current[selectedCartTab];
    if (savedState) {
      // Restore the saved local state instead of loading from cart
      setState(prev => ({
        ...prev,
        selections: savedState.selections,
        selectedTable: savedState.selectedTable,
        selectedTimeSlot: savedState.selectedTimeSlot,
      }));
      // Clear saved state — it's now in the active state
      savedSelectionsPerTab.current[selectedCartTab] = null;
      isTabSwitchingRef.current = false;
      return;
    }

    const currentCart = getCurrentCart();
    if (!currentCart || !currentCart.items || currentCart.items.length === 0) {
      // Only reset selections if user hasn't manually clicked on a table
      // This prevents clearing user's selection when they're starting a new booking
      if (!tableClicked) {
        setState(prev => ({
          ...prev,
          selections: new Map(),
          selectedTable: null,
          selectedTimeSlot: null,
        }));
      }
      isTabSwitchingRef.current = false;
      return;
    }

    // Build selections map from cart items
    const newSelections = new Map<string, Map<string, TableTimeSlotSelection>>();

    // Find the specific cart item if cartItemId is provided
    let targetCartItem: CartItemResponse | null = null;
    if (cartItemId) {
      targetCartItem = currentCart.items.find((item: CartItemResponse) => item.id === cartItemId) || null;
    }

    currentCart.items.forEach((item: CartItemResponse) => {
      if (!item.table_number || !item.booking_time) {
        return; // Skip items without table/time
      }

      const tableNumber = item.table_number;
      const timeSlotKey = getTimeSlotKey(item.booking_time);

      // Derive slot time from the booking_time using the schedule/slot duration
      // (no longer relies on availability response for time data)
      const slotTime = resolveSlotTimeFromBookingTime(timeSlotKey);

      if (slotTime) {
        if (!newSelections.has(tableNumber)) {
          newSelections.set(tableNumber, new Map());
        }

        const tableSelections = newSelections.get(tableNumber)!;
        // Expand quantities: if quantity is 3, add the add_on_id 3 times
        const addOnIdsPerGuest = item.add_ons_per_guest?.map(guestAddOns => {
          const expandedIds: string[] = [];
          guestAddOns?.forEach(addOn => {
            if (addOn) {
              const qty = addOn.quantity || 1;
              for (let i = 0; i < qty; i++) {
                expandedIds.push(addOn.add_on_id);
              }
            }
          });
          return expandedIds;
        }) || [];

        tableSelections.set(timeSlotKey, {
          guestCount: item.total_guests,
          addOnIdsPerGuest: addOnIdsPerGuest,
          cartItemId: item.id,
          startTime: slotTime.startTime,
          duration: slotTime.duration,
          endTime: slotTime.endTime,
        });
      }
    });

    // Set selected table/time - prioritize the target cart item if provided
    // BUT only if user hasn't manually clicked a different table
    let selectedTable: string | null = null;
    let selectedTimeSlot: string | null = null;

    if (targetCartItem && targetCartItem.table_number && targetCartItem.booking_time) {
      // Select the specific cart item's table and time slot
      selectedTable = targetCartItem.table_number;
      selectedTimeSlot = getTimeSlotKey(targetCartItem.booking_time);
    } else if (newSelections.size > 0) {
      // Fallback to first selection if no target cart item
      const firstTableKey = newSelections.keys().next().value;
      selectedTable = firstTableKey || null;
      if (selectedTable) {
        const tableSelections = newSelections.get(selectedTable);
        if (tableSelections && tableSelections.size > 0) {
          const firstTimeSlotKey = tableSelections.keys().next().value;
          selectedTimeSlot = firstTimeSlotKey || null;
        }
      }
    }

    setState(prev => {
      // When loading cart items (on initial load or tab switch), replace selections
      // with the cart data. This ensures hasCartChanges returns false immediately
      // after switching tabs (since local state matches the active cart).
      // If the user has manually clicked a table (tableClicked), we preserve their
      // local selections and only fill in missing slots from the cart.
      let finalSelections: Map<string, Map<string, TableTimeSlotSelection>>;

      if (tableClicked) {
        // User has interacted — merge cart data into existing selections
        // only for slots the user hasn't touched
        finalSelections = new Map(prev.selections);
        newSelections.forEach((tableMap, tableKey) => {
          if (!finalSelections.has(tableKey)) {
            finalSelections.set(tableKey, tableMap);
          } else {
            const existingTableMap = finalSelections.get(tableKey)!;
            tableMap.forEach((timeSlotData, timeSlotKey) => {
              const existingSlot = existingTableMap.get(timeSlotKey);
              if (!existingSlot || existingSlot.guestCount === 0) {
                existingTableMap.set(timeSlotKey, timeSlotData);
              }
            });
          }
        });
      } else {
        // No user interaction — fully replace with cart data so that
        // hasCartChanges is false (local state === active cart)
        finalSelections = newSelections;
      }

      return {
        ...prev,
        selections: finalSelections,
        // If user has manually clicked a table, preserve their selection
        // Otherwise, use the precomputed selection from cart items
        selectedTable: tableClicked ? prev.selectedTable : (selectedTable || prev.selectedTable),
        selectedTimeSlot: tableClicked ? prev.selectedTimeSlot : (selectedTimeSlot || prev.selectedTimeSlot),
      };
    });
    isTabSwitchingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incompleteCarts, selectedCartTab, state.eventBookingCategory, state.tableAvailability, state.syncingCart, getCurrentCart, cartItemId]);

  // Calculate available time slots for the event
  // Each slot now includes a slotIndex for mapping to the server's slot-index availability
  const timeSlots = useMemo(() => {
    if (!state.eventSchedule) {
      return [];
    }

    const { start_time, duration: totalDurationNanoseconds } = state.eventSchedule;

    // Fallback: If custom_slot_duration is not provided, the entire event is exactly 1 timeslot
    const slotDurationNanoseconds = state.eventBookingCategory?.custom_slot_duration || totalDurationNanoseconds;

    const slots: { start_time: string; duration: number; slotIndex: number }[] = [];

    const startMs = new Date(start_time).getTime();
    const totalMs = totalDurationNanoseconds / 1e6;
    const slotMs = slotDurationNanoseconds / 1e6;

    let currentMs = startMs;
    const endMs = startMs + totalMs;

    // Safety fallback: if custom_slot_duration is somehow larger than the event duration itself,
    // or if the loop wouldn't run, we must forcefully guarantee at least 1 time slot representing the event
    if (slotMs > totalMs) {
      slots.push({
        start_time: new Date(startMs).toISOString(),
        duration: totalDurationNanoseconds,
        slotIndex: 0,
      });
      return slots;
    }

    let index = 0;
    while (currentMs + slotMs <= endMs + 1000) { // added small epsilon to avoid precision issues
      slots.push({
        start_time: new Date(currentMs).toISOString(),
        duration: slotDurationNanoseconds,
        slotIndex: index,
      });
      currentMs += slotMs;
      index++;
    }

    // Secondary fallback: if no slots were generated due to math quirks
    if (slots.length === 0) {
      slots.push({
        start_time: new Date(startMs).toISOString(),
        duration: totalDurationNanoseconds,
        slotIndex: 0,
      });
    }

    return slots;
  }, [state.eventSchedule, state.eventBookingCategory]);

  // Reverse lookup: timeSlotKey → slotIndex
  const timeSlotKeyToIndex = useMemo(() => {
    const map = new Map<string, number>();
    timeSlots.forEach(slot => {
      map.set(getTimeSlotKey(slot.start_time), slot.slotIndex);
    });
    return map;
  }, [timeSlots]);

  // Resolve slot time data from a slot index
  const resolveSlotTime = useCallback((slotIndex: number): { startTime: string; duration: number; endTime: string } | null => {
    const slot = timeSlots[slotIndex];
    if (!slot) return null;
    return {
      startTime: slot.start_time,
      duration: slot.duration,
      endTime: calculateEndTime(slot.start_time, slot.duration),
    };
  }, [timeSlots]);

  // Resolve slot time data from a timeSlotKey (for cart preselection)
  const resolveSlotTimeFromBookingTime = useCallback((timeSlotKey: string): { startTime: string; duration: number; endTime: string } | null => {
    const slotIndex = timeSlotKeyToIndex.get(timeSlotKey);
    if (slotIndex === undefined) return null;
    return resolveSlotTime(slotIndex);
  }, [timeSlotKeyToIndex, resolveSlotTime]);

  // Visible time slots: prune slots that have zero availability across ALL tables
  // and are not referenced by any cart item's booking_time
  const visibleTimeSlots = useMemo(() => {
    // Collect all slot indices that appear in ANY table's availability
    const availableIndices = new Set<number>();
    Object.values(state.tableAvailability).forEach(indices => {
      indices.forEach(i => availableIndices.add(i));
    });

    // Collect all slot keys referenced by current cart items
    const cartSlotKeys = new Set<string>();
    const cart = getCurrentCart();
    if (cart?.items) {
      cart.items.forEach(item => {
        if (item.booking_time) {
          cartSlotKeys.add(getTimeSlotKey(item.booking_time));
        }
      });
    }

    // Keep a slot if it's in ANY table's availability OR referenced by a cart item
    return timeSlots.filter(slot => {
      if (availableIndices.has(slot.slotIndex)) return true;
      if (cartSlotKeys.has(getTimeSlotKey(slot.start_time))) return true;
      return false;
    });
  }, [timeSlots, state.tableAvailability, getCurrentCart]);

  // Tables that are in the user's cart for the selected slot but NOT in availability (sold out)
  const soldOutCartTables = useMemo(() => {
    const result: string[] = [];
    if (!state.selectedTimeSlot) return result;

    const slotIndex = timeSlotKeyToIndex.get(state.selectedTimeSlot);
    if (slotIndex === undefined) return result;

    const cart = getCurrentCart();
    if (!cart?.items) return result;

    cart.items.forEach(item => {
      if (!item.table_number || !item.booking_time) return;
      const itemSlotKey = getTimeSlotKey(item.booking_time);
      if (itemSlotKey !== state.selectedTimeSlot) return;

      // Table is in cart for this slot, but NOT in availability
      const tableSlotIndices = state.tableAvailability[item.table_number] || [];
      if (!tableSlotIndices.includes(slotIndex)) {
        result.push(item.table_number);
      }
    });

    return result;
  }, [state.selectedTimeSlot, state.tableAvailability, getCurrentCart, timeSlotKeyToIndex]);

  // Auto-select first time slot and first available table on first visit (when no cart items exist)
  useEffect(() => {
    // Skip if currently syncing or loading
    if (state.syncingCart || isSyncingCartRef.current || state.loading) {
      return;
    }

    const currentCart = getCurrentCart();
    const hasCartItems = !!(currentCart && currentCart.items && currentCart.items.length > 0);

    if (
      !hasAttemptedAutoSelectionRef.current &&
      !hasCartItems &&
      !state.selectedTimeSlot &&
      timeSlots.length > 0 &&
      !isLoadingCarts
    ) {
      const firstTimeSlotKey = getTimeSlotKey(timeSlots[0].start_time);
      const firstSlotIndex = timeSlots[0].slotIndex;

      // Find the first available table for this timeslot using slot indices
      let firstAvailableTable: string | null = null;
      if (state.tableAvailability) {
        // Sort table numbers to pick the "first" in a predictable way
        const sortedTableNumbers = Object.keys(state.tableAvailability).sort();
        for (const tableNumber of sortedTableNumbers) {
          const slotIndices = state.tableAvailability[tableNumber];
          if (slotIndices.includes(firstSlotIndex)) {
            firstAvailableTable = tableNumber;
            break;
          }
        }
      }

      setState(prev => {
        const nextState = { ...prev, selectedTimeSlot: firstTimeSlotKey };

        // If we found an available table, select it natively
        if (firstAvailableTable) {
          nextState.selectedTable = firstAvailableTable;

          // Initialize selection map for the table/timeslot
          const newSelections = new Map(prev.selections);
          if (!newSelections.has(firstAvailableTable)) {
            newSelections.set(firstAvailableTable, new Map());
          }

          const tableSelections = newSelections.get(firstAvailableTable)!;
          if (!tableSelections.has(firstTimeSlotKey)) {
            const slotTime = resolveSlotTime(firstSlotIndex);

            if (slotTime) {
              tableSelections.set(firstTimeSlotKey, {
                guestCount: 0,
                addOnIdsPerGuest: [],
                startTime: slotTime.startTime,
                duration: slotTime.duration,
                endTime: slotTime.endTime,
              });
            }
          }
          nextState.selections = newSelections;
        }

        return nextState;
      });
      hasAttemptedAutoSelectionRef.current = true; // Mark that we've attempted auto-selection
    }
  }, [
    state.loading,
    state.syncingCart,
    state.selectedTimeSlot,
    timeSlots,
    isLoadingCarts,
    getCurrentCart,
    state.tableAvailability,
  ]);

  // Helper function to get table size for a given table number
  const getTableSize = useCallback((tableNumber: string, eventBookingCategory: EventBookingCategoryData | null): number | null => {
    if (!eventBookingCategory?.booking_category.table_categories) {
      return null;
    }

    for (const tableCategory of eventBookingCategory.booking_category.table_categories) {
      if (tableCategory.table_numbers.includes(tableNumber)) {
        return tableCategory.table_size;
      }
    }

    return null;
  }, []);

  // Calculate disabled slots based on overlaps
  const disabledSlots = useMemo(() => {
    const disabled = new Set<string>();
    const currentTable = state.selectedTable;
    if (!currentTable) return disabled;

    const tableSelections = state.selections.get(currentTable);
    if (!tableSelections) return disabled;

    // For each selected slot, disable overlapping slots
    tableSelections.forEach((selection, timeSlotKey) => {
      if (selection.guestCount > 0) {
        timeSlots.forEach(slot => {
          const slotKey = getTimeSlotKey(slot.start_time);
          if (slotKey !== timeSlotKey &&
            doTimeSlotsOverlap(selection.startTime, selection.duration, slot.start_time, slot.duration)) {
            disabled.add(slotKey);
          }
        });
      }
    });

    return disabled;
  }, [state.selectedTable, state.selections, state.tableAvailability, timeSlots]);

  // Calculate price breakdown
  const priceBreakdown = useMemo((): TableBookingPriceBreakdown => {
    if (!state.eventBookingCategory) {
      return {
        perSlot: [],
        totalPrice: 0,
        totalBasePrice: 0,
        totalAddOnPrice: 0,
      };
    }

    const basePrice = state.eventBookingCategory.price;
    const perSlot: TableBookingPriceBreakdown['perSlot'] = [];
    let totalBasePrice = 0;
    let totalAddOnPrice = 0;

    state.selections.forEach((tableSelections, tableNumber) => {
      // Get table size for this table - used for base price calculation
      const tableSizeForPricing = getTableSize(tableNumber, state.eventBookingCategory);

      tableSelections.forEach((selection, timeSlotKey) => {
        if (selection.guestCount > 0) {
          // For table bookings, calculate base price using table size (full table capacity)
          // regardless of actual guest count, since customers pay for the full table
          const priceMultiplier = tableSizeForPricing ?? selection.guestCount;
          const slotBasePrice = basePrice * priceMultiplier;
          totalBasePrice += slotBasePrice;

          let slotAddOnPrice = 0;
          const perGuestBreakdown = selection.addOnIdsPerGuest.map((guestAddOns, guestIndex) => {
            const guestAddOnDetails = guestAddOns.map(addOnId => {
              const addOnSchedule = state.eventBookingCategory?.add_on_schedules.find(
                aos => aos.add_on.id === addOnId
              );
              if (!addOnSchedule) return null;

              return {
                addOn: addOnSchedule,
                quantity: 1,
                price: addOnSchedule.price
              };
            }).filter(Boolean) as Array<{
              addOn: AddOnScheduleData;
              quantity: number;
              price: number;
            }>;

            const guestAddOnPrice = guestAddOnDetails.reduce((sum, item) => sum + item.price, 0);
            slotAddOnPrice += guestAddOnPrice;

            return {
              guestIndex,
              addOns: guestAddOnDetails,
              totalAddOnPrice: guestAddOnPrice
            };
          });

          totalAddOnPrice += slotAddOnPrice;

          perSlot.push({
            tableNumber,
            tableSize: tableSizeForPricing ?? selection.guestCount,
            timeSlotKey,
            startTime: selection.startTime,
            endTime: selection.endTime,
            basePrice: slotBasePrice,
            addOnPrice: slotAddOnPrice,
            totalPrice: slotBasePrice + slotAddOnPrice,
            guestCount: selection.guestCount,
            perGuestBreakdown,
          });
        }
      });
    });

    return {
      perSlot,
      totalPrice: totalBasePrice + totalAddOnPrice,
      totalBasePrice,
      totalAddOnPrice,
    };
  }, [state.eventBookingCategory, state.selections, getTableSize]);

  // Actions
  const selectTable = async (tableNumber: string) => {
    setTableClicked(true); // Mark that user has manually clicked on a table

    setState(prev => {
      const timeSlotKey = prev.selectedTimeSlot;

      const newSelections = new Map(prev.selections);

      // Initialize table map if needed
      if (!newSelections.has(tableNumber)) {
        newSelections.set(tableNumber, new Map());
      }

      const tableSelections = newSelections.get(tableNumber)!;

      // If we found a slot, initialize it if needed
      if (timeSlotKey) {
        if (!tableSelections.has(timeSlotKey)) {
          const slotIndex = timeSlotKeyToIndex.get(timeSlotKey);
          const slotTime = slotIndex !== undefined ? resolveSlotTime(slotIndex) : null;

          if (slotTime) {
            tableSelections.set(timeSlotKey, {
              guestCount: 0,
              addOnIdsPerGuest: [],
              startTime: slotTime.startTime,
              duration: slotTime.duration,
              endTime: slotTime.endTime,
            });
          }
        }
      }

      return {
        ...prev,
        selectedTable: tableNumber,
        selections: newSelections,
      };
    });
  };

  const selectTimeSlot = (timeSlotKey: string) => {
    setState(prev => {
      let newSelectedTable = prev.selectedTable;

      // If a table is already selected, check if it's available in the new timeslot
      if (newSelectedTable) {
        const slotIdx = timeSlotKeyToIndex.get(timeSlotKey);
        const slotIndices = prev.tableAvailability[newSelectedTable] || [];
        const isAvailable = slotIdx !== undefined && slotIndices.includes(slotIdx);
        if (!isAvailable) {
          newSelectedTable = null;
        }
      }

      const newSelections = new Map(prev.selections);

      if (newSelectedTable) {
        // Initialize table selection if needed
        if (!newSelections.has(newSelectedTable)) {
          newSelections.set(newSelectedTable, new Map());
        }

        const tableSelections = newSelections.get(newSelectedTable)!;

        // Initialize time slot if not exists
        if (!tableSelections.has(timeSlotKey)) {
          const slotIndex = timeSlotKeyToIndex.get(timeSlotKey);
          const slotTime = slotIndex !== undefined ? resolveSlotTime(slotIndex) : null;

          if (slotTime) {
            tableSelections.set(timeSlotKey, {
              guestCount: 0, // Start at 0 for table booking
              addOnIdsPerGuest: [],
              startTime: slotTime.startTime,
              duration: slotTime.duration,
              endTime: slotTime.endTime,
            });
          }
        }
      }

      return {
        ...prev,
        selections: newSelections,
        selectedTimeSlot: timeSlotKey,
        selectedTable: newSelectedTable,
      };
    });
  };

  const setGuestCountForSlot = (tableNumber: string, timeSlotKey: string, count: number) => {
    if (count < 0) return;

    // Enforce table size limit - users cannot select more guests than the table size
    const tableSize = getTableSize(tableNumber, state.eventBookingCategory);
    if (tableSize !== null && count > tableSize) {
      return; // Don't allow more guests than table size
    }

    setState(prev => {
      const newSelections = new Map(prev.selections);
      if (!newSelections.has(tableNumber)) {
        newSelections.set(tableNumber, new Map());
      }

      const tableSelections = newSelections.get(tableNumber)!;
      const currentSelection = tableSelections.get(timeSlotKey);

      if (currentSelection) {
        let newAddOnIdsPerGuest: string[][];

        if (count > currentSelection.guestCount) {
          // Find all unique addons that have been selected for any guest
          // New guests should receive all these addons (one of each)
          const allSelectedAddOnIds = new Set<string>();

          currentSelection.addOnIdsPerGuest.forEach(guestAddOns => {
            guestAddOns.forEach(addOnId => {
              allSelectedAddOnIds.add(addOnId);
            });
          });

          // Create new guests with all selected addons (each gets one of each addon)
          const selectedAddOnsArray = Array.from(allSelectedAddOnIds);
          const newGuests = Array(count - currentSelection.guestCount)
            .fill(null)
            .map(() => [...selectedAddOnsArray]);

          newAddOnIdsPerGuest = [...currentSelection.addOnIdsPerGuest, ...newGuests];
        } else {
          newAddOnIdsPerGuest = currentSelection.addOnIdsPerGuest.slice(0, count);
        }

        tableSelections.set(timeSlotKey, {
          ...currentSelection,
          guestCount: count,
          addOnIdsPerGuest: newAddOnIdsPerGuest,
        });

        // Keep the selection even when count is 0, so components remain visible
      }

      return {
        ...prev,
        selections: newSelections,
      };
    });
  };

  const handleAddOnToggleForSlot = (
    tableNumber: string,
    timeSlotKey: string,
    addOnId: string,
    isSelected: boolean,
    guestIndex?: number
  ) => {
    // Check inventory before allowing addition
    if (isSelected) {
      // Calculate adjusted inventory for this add-on
      const addOnSchedule = state.eventBookingCategory?.add_on_schedules.find(
        aos => aos.add_on.id === addOnId
      );

      if (addOnSchedule) {
        // Count total selected add-ons across ALL selections (excluding current selection)
        let totalSelectedInOtherSelections = 0;
        const currentSelection = state.selections.get(tableNumber)?.get(timeSlotKey);
        let currentSelectionCount = 0;

        // Count in current selection
        if (currentSelection) {
          currentSelection.addOnIdsPerGuest.forEach((guestAddOns) => {
            guestAddOns.forEach((id) => {
              if (id === addOnId) {
                currentSelectionCount++;
              }
            });
          });
        }

        // Count in all other selections
        state.selections.forEach((tableSelections, tNum) => {
          tableSelections.forEach((sel, tKey) => {
            // Exclude current selection from count
            if (tNum === tableNumber && tKey === timeSlotKey) {
              return;
            }
            sel.addOnIdsPerGuest.forEach((guestAddOns) => {
              guestAddOns.forEach((id) => {
                if (id === addOnId) {
                  totalSelectedInOtherSelections++;
                }
              });
            });
          });
        });

        // Count how many we're trying to add
        let countToAdd = 0;
        if (guestIndex !== undefined) {
          // Adding for specific guest - check if they already have it
          const guestAddOns = currentSelection?.addOnIdsPerGuest[guestIndex] || [];
          if (!guestAddOns.includes(addOnId)) {
            countToAdd = 1;
          }
        } else {
          // Adding for all guests - count how many don't already have it
          currentSelection?.addOnIdsPerGuest.forEach((guestAddOns) => {
            if (!guestAddOns.includes(addOnId)) {
              countToAdd++;
            }
          });
        }

        // If nothing to add, allow the toggle (it's a removal)
        if (countToAdd === 0) {
          // This is fine - we're either removing or already have it
        } else {
          // limit how much we add based on adjustedInventory
          const isUnlimited = addOnSchedule.remaining_inventory === null;
          let adjustedInventory = isUnlimited
            ? Infinity
            : (addOnSchedule.remaining_inventory as number) - totalSelectedInOtherSelections - currentSelectionCount;

          if (!isUnlimited && adjustedInventory <= 0) {
            setAddOnErrorMessage('Add-on is out of stock!');
            if (errorMessageTimeoutRef.current) clearTimeout(errorMessageTimeoutRef.current);
            errorMessageTimeoutRef.current = setTimeout(() => setAddOnErrorMessage(null), 3000);
            return;
          }

          if (!isUnlimited && adjustedInventory < countToAdd) {
            setAddOnErrorMessage(`Only ${adjustedInventory} left. Added to the first ${adjustedInventory} guests. Tap the card to manually assign.`);
            if (errorMessageTimeoutRef.current) clearTimeout(errorMessageTimeoutRef.current);
            errorMessageTimeoutRef.current = setTimeout(() => setAddOnErrorMessage(null), 4000);
            countToAdd = adjustedInventory; // Process a partial toggle
          }
        }
      }
    }

    setState(prev => {
      const newSelections = new Map(prev.selections);
      if (!newSelections.has(tableNumber)) {
        return prev;
      }

      const tableSelections = newSelections.get(tableNumber)!;
      const selection = tableSelections.get(timeSlotKey);
      if (!selection || selection.guestCount === 0) {
        return prev;
      }

      const newAddOnIdsPerGuest = [...selection.addOnIdsPerGuest];

      let effectiveCountToAdd = 0;

      if (guestIndex !== undefined) {
        // Toggle for specific guest
        const guestAddOns = [...newAddOnIdsPerGuest[guestIndex]];
        const addOnIndex = guestAddOns.indexOf(addOnId);
        if (addOnIndex > -1) {
          guestAddOns.splice(addOnIndex, 1);
        } else {
          guestAddOns.push(addOnId);
        }
        newAddOnIdsPerGuest[guestIndex] = guestAddOns;
      } else {
        // Recalculate how many we can add based on inventory limits fetched earlier
        // Determine how many we actually wanted to add again, and cap it.
        let targetCountToAdd = 0;
        newAddOnIdsPerGuest.forEach((guestAddOns) => {
          if (!guestAddOns.includes(addOnId)) {
            targetCountToAdd++;
          }
        });

        // Compute remaining inventory again to enforce limits
        let totalSelectedInOtherSelections = 0;
        let currentSelectionCount = 0;

        newAddOnIdsPerGuest.forEach((guestAddOns) => {
          guestAddOns.forEach((id) => {
            if (id === addOnId) currentSelectionCount++;
          });
        });

        prev.selections.forEach((tableSelections, tNum) => {
          tableSelections.forEach((sel, tKey) => {
            if (tNum === tableNumber && tKey === timeSlotKey) return;
            sel.addOnIdsPerGuest.forEach((guestAddOns) => {
              guestAddOns.forEach((id) => {
                if (id === addOnId) totalSelectedInOtherSelections++;
              });
            });
          });
        });

        const addOnSchedule = prev.eventBookingCategory?.add_on_schedules.find(aos => aos.add_on.id === addOnId);
        let adjustedInventory = targetCountToAdd;
        if (addOnSchedule) {
          if (addOnSchedule.remaining_inventory === null) {
            adjustedInventory = targetCountToAdd; // Unlimited
          } else {
            adjustedInventory = (addOnSchedule.remaining_inventory as number) - totalSelectedInOtherSelections - currentSelectionCount;
          }
        }
        effectiveCountToAdd = Math.min(targetCountToAdd, Math.max(0, adjustedInventory));

        // Toggle for all (or partially if effectiveCountToAdd is reduced)
        if (isSelected) {
          let addedCount = 0;
          newAddOnIdsPerGuest.forEach((_, idx) => {
            if (!newAddOnIdsPerGuest[idx].includes(addOnId) && addedCount < effectiveCountToAdd) {
              newAddOnIdsPerGuest[idx] = [...newAddOnIdsPerGuest[idx], addOnId];
              addedCount++;
            }
          });
        } else {
          newAddOnIdsPerGuest.forEach((_, idx) => {
            newAddOnIdsPerGuest[idx] = newAddOnIdsPerGuest[idx].filter(id => id !== addOnId);
          });
        }
      }

      tableSelections.set(timeSlotKey, {
        ...selection,
        addOnIdsPerGuest: newAddOnIdsPerGuest,
      });

      return {
        ...prev,
        selections: newSelections,
      };
    });
  };

  // Set specific quantity of an add-on for a specific guest in a specific time slot
  const setAddOnQuantityForSlot = (
    tableNumber: string,
    timeSlotKey: string,
    addOnId: string,
    guestIndex: number,
    quantity: number
  ) => {
    setState(prev => {
      // Deep clone the selections Map to ensure immutability
      const newSelections = new Map<string, Map<string, TableTimeSlotSelection>>();
      prev.selections.forEach((tableSelections, tableNum) => {
        const newTableSelections = new Map<string, TableTimeSlotSelection>();
        tableSelections.forEach((selection, slotKey) => {
          newTableSelections.set(slotKey, { ...selection, addOnIdsPerGuest: [...selection.addOnIdsPerGuest.map(g => [...g])] });
        });
        newSelections.set(tableNum, newTableSelections);
      });

      const tableSelections = newSelections.get(tableNumber);
      if (!tableSelections) return prev;

      const selection = tableSelections.get(timeSlotKey);
      if (!selection || selection.guestCount === 0) {
        return prev;
      }

      // Find the add-on to check inventory
      const addOnSchedule = prev.eventBookingCategory?.add_on_schedules.find(
        aos => aos.add_on.id === addOnId
      );
      if (!addOnSchedule) return prev;

      const newAddOnIdsPerGuest = [...selection.addOnIdsPerGuest.map(g => [...g])];

      // Calculate current usage by OTHER guests
      let usedByOthers = 0;
      newAddOnIdsPerGuest.forEach((guestAddOns, idx) => {
        if (idx !== guestIndex) {
          usedByOthers += guestAddOns.filter(id => id === addOnId).length;
        }
      });

      // Calculate max quantity for this guest
      const isUnlimited = addOnSchedule.remaining_inventory === null;
      const maxQuantity = isUnlimited
        ? Infinity
        : (addOnSchedule.remaining_inventory as number) - usedByOthers;
      const finalQuantity = Math.min(Math.max(0, quantity), maxQuantity);

      // Remove all instances of this add-on from this guest
      newAddOnIdsPerGuest[guestIndex] = newAddOnIdsPerGuest[guestIndex].filter(id => id !== addOnId);

      // Add the correct number of instances
      for (let i = 0; i < finalQuantity; i++) {
        newAddOnIdsPerGuest[guestIndex].push(addOnId);
      }

      tableSelections.set(timeSlotKey, {
        ...selection,
        addOnIdsPerGuest: newAddOnIdsPerGuest,
      });

      return {
        ...prev,
        selections: newSelections,
      };
    });
  };

  const removeTimeSlotSelection = (tableNumber: string, timeSlotKey: string) => {
    setState(prev => {
      // Deep clone the selections Map to ensure immutability
      const newSelections = new Map<string, Map<string, TableTimeSlotSelection>>();
      prev.selections.forEach((tableSelections, tableNum) => {
        const newTableSelections = new Map<string, TableTimeSlotSelection>();
        tableSelections.forEach((selection, slotKey) => {
          newTableSelections.set(slotKey, selection);
        });
        newSelections.set(tableNum, newTableSelections);
      });

      const tableSelections = newSelections.get(tableNumber);

      if (tableSelections) {
        tableSelections.delete(timeSlotKey);

        // If table has no more selections, remove the table entry
        if (tableSelections.size === 0) {
          newSelections.delete(tableNumber);
        }
      }

      // Clear selectedTimeSlot if it matches the removed slot
      const newSelectedTimeSlot = prev.selectedTimeSlot === timeSlotKey ? null : prev.selectedTimeSlot;

      return {
        ...prev,
        selections: newSelections,
        selectedTimeSlot: newSelectedTimeSlot,
      };
    });
  };

  // Check if cart has items for this EBC
  const hasCartItemForEBC = (): boolean => {
    const currentCart = getCurrentCart();
    return !!(currentCart && currentCart.items && currentCart.items.length > 0);
  };

  // Check if there are any selections
  const hasSelections = (): boolean => {
    let hasAny = false;
    state.selections.forEach(tableSelections => {
      tableSelections.forEach(selection => {
        if (selection.guestCount > 0) {
          hasAny = true;
        }
      });
    });
    return hasAny;
  };

  // Check if current UI state differs from cart
  const hasCartChanges = (): boolean => {
    const currentCart = getCurrentCart();
    if (!currentCart || !currentCart.items || currentCart.items.length === 0) {
      return hasSelections(); // If no cart items but we have selections, there are changes
    }

    // Build selections from cart items
    const cartSelections = new Map<string, Map<string, { guestCount: number; addOnIdsPerGuest: string[][]; bookingTime: string }>>();

    currentCart.items.forEach((item: CartItemResponse) => {
      if (!item.table_number || !item.booking_time) return;

      const tableNumber = item.table_number;
      const timeSlotKey = getTimeSlotKey(item.booking_time);

      if (!cartSelections.has(tableNumber)) {
        cartSelections.set(tableNumber, new Map());
      }

      const tableSelections = cartSelections.get(tableNumber)!;
      // Expand quantities: if quantity is 3, add the add_on_id 3 times
      const addOnIdsPerGuest = item.add_ons_per_guest?.map(guestAddOns => {
        const expandedIds: string[] = [];
        guestAddOns?.forEach(addOn => {
          if (addOn) {
            const qty = addOn.quantity || 1;
            for (let i = 0; i < qty; i++) {
              expandedIds.push(addOn.add_on_id);
            }
          }
        });
        return expandedIds;
      }) || [];

      tableSelections.set(timeSlotKey, {
        guestCount: item.total_guests,
        addOnIdsPerGuest: addOnIdsPerGuest,
        bookingTime: item.booking_time,
      });
    });

    // Compare with current selections
    // First, verify all non-empty local selections exist and match in the cart
    for (const [tableNumber, tableSelections] of state.selections.entries()) {
      const cartTableSelections = cartSelections.get(tableNumber);

      for (const [timeSlotKey, selection] of tableSelections.entries()) {
        // Skip empty local selections — they don't count as "changes" needing sync
        if (selection.guestCount === 0) continue;

        if (!cartTableSelections) {
          console.log('hasCartChanges true: cartTableSelections missing for', tableNumber);
          return true; // We have a non-empty selection for a table not in cart
        }

        const cartSelection = cartTableSelections.get(timeSlotKey);
        if (!cartSelection) {
          console.log('hasCartChanges true: cartSelection missing for', timeSlotKey);
          return true; // We have a non-empty selection for a slot not in cart
        }

        if (selection.guestCount !== cartSelection.guestCount) {
          console.log('hasCartChanges true: guestCount mismatch', selection.guestCount, cartSelection.guestCount);
          return true;
        }

        // Compare add-ons per guest
        if (selection.addOnIdsPerGuest.length !== cartSelection.addOnIdsPerGuest.length) {
          console.log('hasCartChanges true: addOnIdsPerGuest length mismatch', selection.addOnIdsPerGuest.length, cartSelection.addOnIdsPerGuest.length);
          return true;
        }

        for (let i = 0; i < selection.addOnIdsPerGuest.length; i++) {
          const uiAddOns = [...(selection.addOnIdsPerGuest[i] || [])].sort();
          const cartAddOns = [...(cartSelection.addOnIdsPerGuest[i] || [])].sort();
          if (uiAddOns.length !== cartAddOns.length) {
            console.log('hasCartChanges true: addOn length mismatch at index', i);
            return true;
          }
          for (let j = 0; j < uiAddOns.length; j++) {
            if (uiAddOns[j] !== cartAddOns[j]) {
              console.log('hasCartChanges true: addOn value mismatch', uiAddOns[j], cartAddOns[j]);
              return true;
            }
          }
        }
      }
    }

    // Second, verify all cart items still exist in local selections
    for (const [tableNumber, cartTableSelections] of cartSelections.entries()) {
      const localTableSelections = state.selections.get(tableNumber);
      if (!localTableSelections) {
        console.log('hasCartChanges true: localTableSelections missing for cart item', tableNumber);
        return true; // A cart item was completely removed locally
      }

      for (const [timeSlotKey] of cartTableSelections.entries()) {
        const localSelection = localTableSelections.get(timeSlotKey);
        if (!localSelection) {
          console.log('hasCartChanges true: localSelection missing for cart time slot', timeSlotKey);
          return true;
        }
        if (localSelection.guestCount === 0) {
          console.log('hasCartChanges true: localSelection guestCount is 0 for cart time slot', timeSlotKey);
          return true; // A cart item was removed or cleared locally
        }
      }
    }

    return false;
  };

  // Switch cart tab — save current tab's local state before switching
  const switchCartTab = (tab: 'main' | 'guest') => {
    // Save current tab's local state so it can be restored later
    if (selectedCartTab && selectedCartTab !== tab) {
      savedSelectionsPerTab.current[selectedCartTab] = {
        selections: state.selections,
        selectedTable: state.selectedTable,
        selectedTimeSlot: state.selectedTimeSlot,
      };
    }
    isTabSwitchingRef.current = true;
    // Reset tableClicked so the effect can load the new tab cleanly
    setTableClicked(false);
    setSelectedCartTab(tab);
  };

  // Check if cart needs checkout cancellation
  const shouldCancelCheckout = (cart: CartResponse | null): boolean => {
    if (!cart) return false;
    return cart.status === CartStatus.PendingPayment || cart.status === CartStatus.FailedPayment;
  };

  // Sync cart items
  const syncCartItems = async (): Promise<{ cartId: string }> => {
    // Verify we have the required data before starting
    if (!state.eventBookingCategory) {
      throw new Error('No booking category selected');
    }

    // Set syncing flag
    setState(prev => ({ ...prev, syncingCart: true }));
    isSyncingCartRef.current = true;

    // Capture current state values at the start of sync
    // We read from state directly - since we set syncingCart to true above,
    // no other state updates should happen that would affect selections
    const latestSelections = state.selections;
    const latestEventBookingCategory = state.eventBookingCategory;
    const latestEventSchedule = state.eventSchedule;

    try {
      // Track if we just registered as guest to determine isGuestCart correctly
      let justRegisteredAsGuest = false;
      let guestUserFromRegistration: { is_guest: boolean } | null = null;

      // For unauthenticated users, register as guest first
      if (!isAuthenticated) {
        try {
          const guestResponse = await authService.registerGuest();
          guestUserFromRegistration = guestResponse.user;
          justRegisteredAsGuest = true;
          await refetchAuth();
        } catch (error) {
          console.error('Error registering guest:', error);
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            throw new Error('Network error. Please check your connection and try again.');
          }
          throw new Error('Failed to register as guest. Please try again or sign in.');
        }
      }

      // After auth refetch (e.g., after registration), refetch carts to get latest data
      // This ensures we have the correct cart data, especially for guest carts after registration
      // We refetch regardless of isAuthenticated check because refetchAuth() may have just updated it
      let freshCartsAfterAuth: CartResponse[] | null = null;
      if (latestEventBookingCategory) {
        try {
          freshCartsAfterAuth = await cartService.getIncompleteCartsForEBC(latestEventBookingCategory.id);

          if (freshCartsAfterAuth.length > 2) {
            console.error(`Unexpected: Found ${freshCartsAfterAuth.length} incomplete carts for EBC. Taking most recent 2.`);
            const sortedCarts = [...freshCartsAfterAuth].sort((a, b) => {
              return new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime();
            });
            freshCartsAfterAuth.splice(0, freshCartsAfterAuth.length, ...sortedCarts.slice(0, 2));
          }

          setIncompleteCarts(freshCartsAfterAuth);
          const defaultTab = determineDefaultTab(freshCartsAfterAuth);
          setSelectedCartTab(defaultTab);
        } catch (error) {
          console.warn('Failed to refetch carts after auth update, using existing cart data:', error);
          // Continue with existing cart data if refetch fails
        }
      }

      // Get current cart - use fresh carts if available, otherwise use getCurrentCart callback
      // Note: We need to use the updated selectedCartTab from the state update above
      let currentCart: CartResponse | null = null;
      const currentTab = selectedCartTab; // Capture current tab value
      if (freshCartsAfterAuth && currentTab) {
        if (currentTab === 'main') {
          currentCart = freshCartsAfterAuth.find(c => !c.is_guest_cart) || null;
        } else {
          currentCart = freshCartsAfterAuth.find(c => c.is_guest_cart) || null;
        }
      } else if (freshCartsAfterAuth) {
        // If we have fresh carts but no tab selected, try to determine from fresh carts
        const defaultTabFromFresh = determineDefaultTab(freshCartsAfterAuth);
        if (defaultTabFromFresh === 'main') {
          currentCart = freshCartsAfterAuth.find(c => !c.is_guest_cart) || null;
        } else if (defaultTabFromFresh === 'guest') {
          currentCart = freshCartsAfterAuth.find(c => c.is_guest_cart) || null;
        }
      } else {
        // Fallback to getCurrentCart if no fresh carts
        currentCart = getCurrentCart();
      }

      // Check if checkout needs to be cancelled
      if (shouldCancelCheckout(currentCart)) {
        try {
          await cartService.cancelCheckout(currentCart!.id);
        } catch (error) {
          console.error('Error canceling checkout:', error);
          throw new Error('Couldn\'t cancel previous checkout. Please try again or contact support.');
        }
      }

      // Determine is_guest_cart
      // Priority order:
      // 1. If user explicitly selected a tab, respect that choice (highest priority)
      // 2. If cart exists, use cart's is_guest_cart property (for updates)
      // 3. If we just registered as guest, use the registration response
      // 4. If authenticated and not guest, default to main cart (isGuestCart = false)
      // 5. Fall back to isGuest from auth context
      let isGuestCart: boolean;
      if (currentTab === 'guest') {
        // User explicitly selected guest tab - respect their choice
        isGuestCart = true;
      } else if (currentTab === 'main') {
        // User explicitly selected main tab - respect their choice
        isGuestCart = false;
      } else if (currentCart) {
        // For existing cart updates, use the cart's is_guest_cart property
        isGuestCart = currentCart.is_guest_cart;
      } else if (justRegisteredAsGuest && guestUserFromRegistration) {
        // If we just registered as guest and have no cart, use the registration response
        isGuestCart = guestUserFromRegistration.is_guest;
      } else if (isAuthenticated && !isGuest) {
        // Authenticated non-guest users default to main cart (isGuestCart = false)
        isGuestCart = false;
      } else {
        // Fall back to auth context
        isGuestCart = isGuest;
      }

      // Helper function to transform addon IDs to AddOnSelection format
      const transformToSelections = (addOnIdsPerGuest: string[][]): AddOnSelection[][] => {
        return addOnIdsPerGuest.map(guestAddOns => {
          const counts = new Map<string, number>();
          guestAddOns.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
          return Array.from(counts.entries()).map(([add_on_id, quantity]) => ({ add_on_id, quantity }));
        });
      };

      // Build selected items from latest selections (captured at start of function)
      const selectedItems: Array<{
        existing_cart_item_id?: string | null;
        table_number?: string | null;
        booking_time?: string | null;
        total_guests: number;
        add_on_selections_per_guest: AddOnSelection[][];
      }> = [];

      latestSelections.forEach((tableSelections, tableNumber) => {
        tableSelections.forEach((selection) => {
          if (selection.guestCount > 0) {
            selectedItems.push({
              existing_cart_item_id: selection.cartItemId || null,
              table_number: tableNumber,
              booking_time: latestEventSchedule?.timezone_id
                ? formatISOWithTimezone(selection.startTime, latestEventSchedule.timezone_id)
                : selection.startTime,
              total_guests: selection.guestCount,
              add_on_selections_per_guest: transformToSelections(selection.addOnIdsPerGuest),
            });
          }
        });
      });

      const syncBody = {
        is_guest_cart: isGuestCart,
        selected_items: selectedItems,
      };

      console.log('Sync API Request Body:', JSON.stringify(syncBody, null, 2));

      const result = await tableBookingTicketService.syncTableBookingCartItems(
        latestEventBookingCategory.id,
        syncBody
      );

      // Refetch carts immediately after sync to update UI
      // This ensures the cart state is updated with the latest data from the sync response
      // Note: We set isSyncingCartRef to false AFTER updating carts to prevent useEffect from overwriting
      if (latestEventBookingCategory) {
        try {
          const freshCarts = await cartService.getIncompleteCartsForEBC(latestEventBookingCategory.id);

          if (freshCarts.length > 2) {
            console.error(`Unexpected: Found ${freshCarts.length} incomplete carts for EBC. Taking most recent 2.`);
            const sortedCarts = [...freshCarts].sort((a, b) => {
              return new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime();
            });
            freshCarts.splice(0, freshCarts.length, ...sortedCarts.slice(0, 2));
          }

          // Update carts and then immediately set syncing flag to false
          // This ensures the useEffect can run after we're done
          // We also need to map the new cart items back to our local selections
          // so that the UI can instantly recognize them as "In Cart" instead of "Draft"
          setIncompleteCarts(freshCarts);
          const defaultTab = determineDefaultTab(freshCarts);
          setSelectedCartTab(defaultTab);

          const newCurrentCart = defaultTab === 'main' ? freshCarts.find(c => !c.is_guest_cart) : freshCarts.find(c => c.is_guest_cart);

          if (newCurrentCart && newCurrentCart.items) {
            setState(prev => {
              const newSelections = new Map<string, Map<string, TableTimeSlotSelection>>();

              prev.selections.forEach((tableSelections, tableNum) => {
                const newTableSelections = new Map<string, TableTimeSlotSelection>();
                tableSelections.forEach((selection, slotKey) => {
                  // Find matching cart item for this table and time slot
                  const matchingCartItem = newCurrentCart.items ? newCurrentCart.items.find(item =>
                    item.table_number === tableNum &&
                    item.booking_time &&
                    getTimeSlotKey(item.booking_time) === slotKey
                  ) : undefined;

                  newTableSelections.set(slotKey, {
                    ...selection,
                    cartItemId: matchingCartItem ? matchingCartItem.id : undefined
                  });
                });
                newSelections.set(tableNum, newTableSelections);
              });

              return { ...prev, selections: newSelections };
            });
          }
        } catch (error) {
          console.warn('Failed to refetch carts after sync, will try full refetch:', error);
          // Fall back to full fetchData if cart refetch fails
          try {
            await fetchData();
          } catch (fetchError) {
            console.warn('Failed to refetch data after sync:', fetchError);
          }
        }
      }

      // Cart count will auto-refresh via header's stale-while-revalidate

      // Clear saved per-tab state for the synced tab — local changes are now in the cart
      if (selectedCartTab) {
        savedSelectionsPerTab.current[selectedCartTab] = null;
      }

      return { cartId: result.id };
    } catch (error) {
      console.error('Error in syncCartItems:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to sync cart items. Please try again.');
    } finally {
      setState(prev => ({ ...prev, syncingCart: false }));
      isSyncingCartRef.current = false;
    }
  };

  // Remove all items (unselect all)
  const removeAllItems = async (): Promise<void> => {
    setState(prev => ({
      ...prev,
      selections: new Map(),
      // Keep selectedTable and selectedTimeSlot so the floor plan remains visible
    }));
    // We intentionally DO NOT reset the tableClicked flag here (setTableClicked(false)).
    // Doing so would cause the cart useEffect to clear the selectedTimeSlot when the cart empties,
    // which unmounts the floor plan and table list views.

    // If there's a cart with items, sync with empty array
    if (hasCartItemForEBC()) {
      await syncCartItems();
    }
  };

  const currentCart = getCurrentCart();
  const hasItemForEBC = hasCartItemForEBC();
  // While a tab switch is in progress (between setSelectedCartTab and the useEffect
  // that syncs selections), suppress hasCartChanges to prevent a brief flash of
  // "Update Cart" caused by stale selections being compared against the new cart.
  const hasChanges = isTabSwitchingRef.current ? false : hasCartChanges();
  const hasAnySelections = hasSelections();


  // Export cart table selections for UI styling
  const getCartTableSelections = useCallback(() => {
    const cartSelections = new Map<string, Map<string, { guestCount: number; addOnIdsPerGuest: string[][]; bookingTime: string }>>();
    const cart = getCurrentCart();

    if (cart && cart.items) {
      cart.items.forEach((item: CartItemResponse) => {
        if (!item.table_number || !item.booking_time) return;

        const tableNumber = item.table_number;
        const timeSlotKey = getTimeSlotKey(item.booking_time);

        if (!cartSelections.has(tableNumber)) {
          cartSelections.set(tableNumber, new Map());
        }

        const tableSelections = cartSelections.get(tableNumber)!;
        const addOnIdsPerGuest = item.add_ons_per_guest?.map(guestAddOns => {
          const expandedIds: string[] = [];
          guestAddOns?.forEach(addOn => {
            if (addOn && addOn.add_on_id) {
              const qty = addOn.quantity || 1;
              for (let i = 0; i < qty; i++) {
                expandedIds.push(addOn.add_on_id);
              }
            }
          });
          return expandedIds;
        }) || [];

        tableSelections.set(timeSlotKey, {
          guestCount: item.total_guests,
          addOnIdsPerGuest: addOnIdsPerGuest,
          bookingTime: item.booking_time,
        });
      });
    }
    return cartSelections;
  }, [getCurrentCart, getTimeSlotKey]);

  // Get available tables for the currently selected time slot
  const availableTables = useMemo(() => {
    if (!state.selectedTimeSlot || !state.tableAvailability) {
      return [];
    }
    const slotIndex = timeSlotKeyToIndex.get(state.selectedTimeSlot);
    if (slotIndex === undefined) return [];

    const available: string[] = [];
    for (const [tableNumber, slotIndices] of Object.entries(state.tableAvailability)) {
      if (slotIndices.includes(slotIndex)) {
        available.push(tableNumber);
      }
    }
    return available.sort();
  }, [state.tableAvailability, state.selectedTimeSlot, timeSlotKeyToIndex]);

  // Get current selection for selected table/time slot
  const currentSelection = useMemo(() => {
    if (!state.selectedTable || !state.selectedTimeSlot) {
      return null;
    }
    return state.selections.get(state.selectedTable)?.get(state.selectedTimeSlot) || null;
  }, [state.selectedTable, state.selectedTimeSlot, state.selections]);

  // Calculate adjusted add-on schedules with local inventory tracking
  // This aggregates selections across all items and reduces available inventory accordingly
  const adjustedAddOnSchedules = useMemo((): AddOnScheduleData[] => {
    if (!state.eventBookingCategory?.add_on_schedules) {
      return [];
    }

    // Count total selected add-ons across ALL selections (all tables/time slots)
    const addOnCounts = new Map<string, number>();

    state.selections.forEach((tableSelections) => {
      tableSelections.forEach((selection) => {
        // Count add-ons in this selection
        selection.addOnIdsPerGuest.forEach((guestAddOns) => {
          guestAddOns.forEach((addOnId) => {
            addOnCounts.set(addOnId, (addOnCounts.get(addOnId) || 0) + 1);
          });
        });
      });
    });

    // Calculate adjusted inventory for each add-on
    // For the current selection, we exclude its own count so it can see what's available
    const currentSelectionCounts = new Map<string, number>();
    if (currentSelection) {
      currentSelection.addOnIdsPerGuest.forEach((guestAddOns) => {
        guestAddOns.forEach((addOnId) => {
          currentSelectionCounts.set(addOnId, (currentSelectionCounts.get(addOnId) || 0) + 1);
        });
      });
    }

    return state.eventBookingCategory.add_on_schedules
      .filter((addOnSchedule) => addOnSchedule.remaining_inventory === null || addOnSchedule.remaining_inventory > 0) // Filter out add-ons with 0 original inventory
      .map((addOnSchedule) => {
        const addOnId = addOnSchedule.add_on.id;
        const totalSelected = addOnCounts.get(addOnId) || 0;
        const currentSelectionCount = currentSelectionCounts.get(addOnId) || 0;

        // Adjusted inventory = original - (total selected - current selection's count)
        // This way the current selection sees what's available for it
        let adjustedInventory: number | null = null;
        if (addOnSchedule.remaining_inventory !== null) {
          adjustedInventory = Math.max(
            0,
            addOnSchedule.remaining_inventory - (totalSelected - currentSelectionCount)
          );
        }

        return {
          ...addOnSchedule,
          remaining_inventory: adjustedInventory,
        };
      })
      .filter((addOnSchedule) => addOnSchedule.remaining_inventory === null || addOnSchedule.remaining_inventory > 0); // Filter out add-ons with 0 adjusted inventory
  }, [
    state.eventBookingCategory?.add_on_schedules,
    state.selections,
    currentSelection,
  ]);

  // Get table size for selected table
  const tableSize = useMemo(() => {
    if (!state.selectedTable) return null;
    return getTableSize(state.selectedTable, state.eventBookingCategory);
  }, [state.selectedTable, state.eventBookingCategory, getTableSize]);

  // Compute the real-time item count for a given tab
  // For the active tab: count local selections with guestCount > 0
  // For the inactive tab: check saved state, then fall back to server-side cart count
  const getItemCountForTab = (tab: 'main' | 'guest'): number => {
    if (tab === selectedCartTab) {
      // Active tab — count from current local state
      let count = 0;
      state.selections.forEach(tableSelections => {
        tableSelections.forEach(selection => {
          if (selection.guestCount > 0) count++;
        });
      });
      return count;
    }

    // Inactive tab — check saved local state first
    const saved = savedSelectionsPerTab.current[tab];
    if (saved) {
      let count = 0;
      saved.selections.forEach(tableSelections => {
        tableSelections.forEach(selection => {
          if (selection.guestCount > 0) count++;
        });
      });
      return count;
    }

    // Fall back to server-side cart item count
    const cart = incompleteCarts?.find(c =>
      tab === 'main' ? !c.is_guest_cart : c.is_guest_cart
    );
    return cart?.items?.length ?? 0;
  };

  return {
    ...state,
    tableAvailability: state.tableAvailability,
    timeSlots: visibleTimeSlots,
    priceBreakdown,
    availableTables,
    currentSelection,
    adjustedAddOnSchedules,
    soldOutCartTables,
    selectTable,
    selectTimeSlot,
    setGuestCountForSlot,
    handleAddOnToggleForSlot,
    setAddOnQuantityForSlot,
    removeTimeSlotSelection,
    syncCartItems,
    removeAllItems,
    // Cart-related exports
    incompleteCarts,
    selectedCartTab,
    currentCart,
    isLoadingCarts,
    hasCartItemForEBC: hasItemForEBC,
    hasCartChanges: hasChanges,
    hasSelections: hasAnySelections,
    switchCartTab,
    addOnErrorMessage,
    disabledSlots,
    getCartTableSelections,
    getItemCountForTab,
    // Table size for validation
    tableSize,
    // User interaction tracking
    tableClicked,
    // Refetch function
    refetch: fetchData,
  };
};

